import { MarkerType, type Edge, type Node } from '@xyflow/react';
import type { DatiNodo } from '../../components/email/automazioni/NodoPasso';
import {
  DOMANDE,
  PASSI,
  type ConfigAttendiEvento,
  type ConfigAttesa,
  type ConfigCondizione,
  type ConfigEtichetta,
  type ConfigIngresso,
  type ConfigInvio,
  type EmailAutomationStep,
  type StepKind,
} from '../../types/automazioni.types';

/**
 * Il ponte fra il disegno sulla lavagna e le righe del database.
 *
 * Sulla lavagna un percorso e' fatto di caselle e frecce; nel database e'
 * fatto di righe che si puntano a vicenda. Qui si traduce nei due sensi, e
 * solo qui: e' l'unico punto in cui un errore puo' trasformare un percorso
 * giusto in uno sbagliato, quindi e' anche l'unico da tenere d'occhio.
 */

/** La casella di partenza non sta nel database: e' il punto da cui parte la
 *  freccia verso first_step_id. Sulla lavagna pero' si deve vedere. */
export const ID_INIZIO = 'inizio';

export interface Grafo {
  nodi: Node<DatiNodo>[];
  archi: Edge[];
}

export interface NomiEtichette {
  [id: string]: string;
}

/** Una durata in minuti detta come la si direbbe: "3 giorni", "1 ora". */
function durataLeggibile(minuti: number): string {
  if (minuti % (60 * 24) === 0) {
    const giorni = minuti / (60 * 24);
    return giorni === 1 ? '1 giorno' : `${giorni} giorni`;
  }
  if (minuti % 60 === 0) {
    const ore = minuti / 60;
    return ore === 1 ? '1 ora' : `${ore} ore`;
  }
  return `${minuti} minuti`;
}

/** Il tempo massimo di "aspetta che entri in una lista". Null = senza scadenza. */
function massimoAttesa(passo: EmailAutomationStep): number | null {
  const massimo = (passo.config as ConfigAttendiEvento).max_minuti;
  return massimo === null || massimo === undefined ? null : Number(massimo);
}

/**
 * Vero se l'attesa di una lista ha un tempo massimo, e quindi l'uscita
 * "Scaduto". Conta la spunta, non il numero: mentre lo si riscrive il campo
 * passa da zero, e la freccia "Scaduto" non deve staccarsi (lo zero lo
 * segnala cosaManca).
 */
export function haScadenza(passo: EmailAutomationStep): boolean {
  return massimoAttesa(passo) !== null;
}

/** Da "2026-10-30" a "30/10/2026". */
function dataItaliana(data: string): string {
  const [anno, mese, giorno] = data.split('-');
  return `${giorno}/${mese}/${anno}`;
}

/** Una riga che dice cosa fa la casella, senza doverla aprire. */
export function riassuntoPasso(passo: EmailAutomationStep, etichette: NomiEtichette): string {
  const nomeLista = (tagId: string | null | undefined) =>
    tagId ? etichette[tagId] ?? 'lista eliminata' : null;

  switch (passo.kind) {
    case 'invio': {
      const config = passo.config as ConfigInvio;
      return config.subject ? `"${config.subject}"` : 'Oggetto da scrivere';
    }
    case 'attesa': {
      const minuti = Number((passo.config as ConfigAttesa).minuti ?? 0);
      return minuti <= 0 ? 'Durata da scegliere' : durataLeggibile(minuti);
    }
    case 'attendi_evento': {
      const nome = nomeLista((passo.config as ConfigAttendiEvento).tag_id);
      if (!nome) return 'Lista da scegliere';
      const massimo = massimoAttesa(passo);
      const fino = massimo && massimo > 0 ? `al massimo ${durataLeggibile(massimo)}` : 'senza scadenza';
      return `Finche' entra in "${nome}" · ${fino}`;
    }
    case 'ingresso_evento': {
      const nome = nomeLista((passo.config as ConfigIngresso).tag_id);
      return nome ? `Ogni volta che entra in "${nome}"` : 'Lista da scegliere';
    }
    case 'condizione': {
      const config = passo.config as ConfigCondizione;
      if (config.tipo === 'prima_di' && config.data) {
        return `E' prima del ${dataItaliana(config.data)}?`;
      }
      return config.tipo ? DOMANDE[config.tipo].label : 'Domanda da scegliere';
    }
    case 'etichetta': {
      const config = passo.config as ConfigEtichetta;
      const nome = nomeLista(config.tag_id);
      if (!nome) return 'Lista da scegliere';
      if (config.azione === 'solo_se_dentro') return `Solo chi e' in "${nome}"`;
      return config.azione === 'togli' ? `Toglie da "${nome}"` : `Mette in "${nome}"`;
    }
    case 'fine':
      return 'Il percorso si ferma qui';
  }
}

/**
 * Cosa manca alla casella per poter accendere la sequenza, detto in una riga.
 * Null se non manca niente. Si legge direttamente sulla casella, cosi' non
 * serve un elenco di avvisi a parte per capire dove mettere le mani.
 */
export function cosaManca(passo: EmailAutomationStep): string | null {
  switch (passo.kind) {
    case 'invio': {
      const config = passo.config as ConfigInvio;
      if (!config.subject?.trim()) return 'Manca l\'oggetto';
      if ((config.document?.blocchi?.length ?? 0) === 0) return 'La email e\' vuota';
      return null;
    }
    case 'attesa':
      return Number((passo.config as ConfigAttesa).minuti ?? 0) <= 0 ? 'Manca la durata' : null;
    case 'attendi_evento': {
      if (!(passo.config as ConfigAttendiEvento).tag_id) return 'Manca la lista';
      const massimo = massimoAttesa(passo);
      return massimo !== null && !(massimo > 0) ? 'Manca il tempo massimo' : null;
    }
    case 'ingresso_evento':
      return (passo.config as ConfigIngresso).tag_id ? null : 'Manca la lista';
    case 'condizione': {
      const config = passo.config as ConfigCondizione;
      if (!config.tipo) return 'Manca la domanda';
      if (config.tipo === 'prima_di' && !config.data) return 'Manca la data';
      return null;
    }
    case 'etichetta':
      return (passo.config as ConfigEtichetta).tag_id ? null : 'Manca la lista';
    case 'fine':
      return null;
  }
}

/** Vero se alla casella manca qualcosa per poter accendere la sequenza. */
export function passoIncompleto(passo: EmailAutomationStep): boolean {
  return cosaManca(passo) !== null;
}

/** Quello che la casella mostra sulla lavagna. */
export function datiNodo(passo: EmailAutomationStep, etichette: NomiEtichette): DatiNodo {
  return {
    kind: passo.kind,
    titolo: passo.label || PASSI[passo.kind].label,
    riassunto: riassuntoPasso(passo, etichette),
    manca: cosaManca(passo),
    ...(passo.kind === 'attendi_evento' ? { conScadenza: haScadenza(passo) } : {}),
  };
}

/**
 * Le caselle da cui si parte: nessuna freccia ci arriva, e non sono mai
 * "non collegate". "Si parte" e le azioni "ogni volta che entra in una lista".
 */
export function eUnaPartenza(kind: DatiNodo['kind']): boolean {
  return kind === 'inizio' || kind === 'ingresso_evento';
}

/**
 * Da quali attacchi escono le due frecce di una casella: quella principale
 * (next_id) e l'alternativa (next_alt_id), se la casella ce l'ha. L'attesa di
 * una lista ha "Scaduto" solo con un tempo massimo: senza, non scadrebbe mai.
 */
export function attacchi(passo: EmailAutomationStep): { principale: string; alternativa: string | null } {
  if (passo.kind === 'condizione') return { principale: 'si', alternativa: 'no' };
  if (passo.kind === 'attendi_evento') {
    return { principale: 'arrivata', alternativa: haScadenza(passo) ? 'scaduta' : null };
  }
  return { principale: 'out', alternativa: null };
}

/** Il tipo di freccia disegnato da FrecciaSequenza. */
export const TIPO_FRECCIA = 'freccia';

/**
 * Il colore della freccia dice il ramo: verde il si' e la lista arrivata,
 * rosso il no, arancio il tempo scaduto, grigio il resto.
 */
export const COLORE_RAMO: Record<string, string> = {
  out: '#a3a3a3',
  si: '#16a34a',
  no: '#dc2626',
  arrivata: '#16a34a',
  scaduta: '#ea580c',
};

/** La scritta sulla freccia di un ramo. Le frecce semplici non ne hanno. */
export const NOME_RAMO: Record<string, string> = {
  si: "Si'",
  no: 'No',
  arrivata: 'Arrivata',
  scaduta: 'Scaduto',
};

/** Una freccia da un attacco di una casella a un'altra casella. */
export function freccia(source: string, sourceHandle: string, target: string): Edge {
  return {
    id: `${source}-${sourceHandle}-${target}`,
    type: TIPO_FRECCIA,
    source,
    sourceHandle,
    target,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: COLORE_RAMO[sourceHandle] ?? COLORE_RAMO.out,
      width: 18,
      height: 18,
    },
  };
}

/** Dal database alla lavagna. */
export function passiInGrafo(
  passi: EmailAutomationStep[],
  firstStepId: string | null,
  etichette: NomiEtichette = {},
  partenza = 'Da qui entrano i contatti'
): Grafo {
  const nodi: Node<DatiNodo>[] = [
    {
      id: ID_INIZIO,
      type: 'passo',
      position: { x: 0, y: -140 },
      deletable: false,
      data: {
        kind: 'inizio',
        titolo: 'Si parte',
        riassunto: partenza,
      },
    },
    ...passi.map((passo) => ({
      id: passo.id,
      type: 'passo',
      position: { x: Number(passo.pos_x), y: Number(passo.pos_y) },
      data: datiNodo(passo, etichette),
    })),
  ];

  const archi: Edge[] = [];
  const esistenti = new Set(passi.map((p) => p.id));

  if (firstStepId && esistenti.has(firstStepId)) {
    archi.push(freccia(ID_INIZIO, 'out', firstStepId));
  }

  for (const passo of passi) {
    const uscite = attacchi(passo);
    if (passo.next_id && esistenti.has(passo.next_id)) {
      archi.push(freccia(passo.id, uscite.principale, passo.next_id));
    }
    if (uscite.alternativa && passo.next_alt_id && esistenti.has(passo.next_alt_id)) {
      archi.push(freccia(passo.id, uscite.alternativa, passo.next_alt_id));
    }
  }

  return { nodi, archi };
}

/** Dalla lavagna al database: le frecce tornano a essere colonne. */
export function grafoInPassi(
  passi: EmailAutomationStep[],
  nodi: Node<DatiNodo>[],
  archi: Edge[]
): { passi: EmailAutomationStep[]; firstStepId: string | null } {
  const posizioni = new Map(nodi.map((n) => [n.id, n.position]));

  const inizio = archi.find((a) => a.source === ID_INIZIO);
  const firstStepId = inizio?.target ?? null;

  const aggiornati = passi.map((passo) => {
    const posizione = posizioni.get(passo.id);
    const uscite = archi.filter((a) => a.source === passo.id);
    const quali = attacchi(passo);

    // Una freccia rimasta su un attacco che la casella non ha piu' (tolto il
    // tempo massimo, sparisce "Scaduto") non si salva.
    const principale = uscite.find((a) => (a.sourceHandle ?? 'out') === quali.principale);
    const alternativa = quali.alternativa
      ? uscite.find((a) => a.sourceHandle === quali.alternativa)
      : undefined;

    return {
      ...passo,
      pos_x: posizione?.x ?? passo.pos_x,
      pos_y: posizione?.y ?? passo.pos_y,
      next_id: principale?.target ?? null,
      next_alt_id: alternativa?.target ?? null,
    };
  });

  return { passi: aggiornati, firstStepId };
}

/** Una casella nuova, gia' pronta per essere appoggiata sulla lavagna. */
export function passoNuovo(
  automationId: string,
  kind: StepKind,
  posizione: { x: number; y: number }
): EmailAutomationStep {
  const config: Record<StepKind, EmailAutomationStep['config']> = {
    invio: { subject: '', preheader: '', document: { versione: 1, blocchi: [] } },
    attesa: { minuti: 60 * 24 * 3 },
    attendi_evento: { tag_id: null, max_minuti: null },
    condizione: { tipo: 'ha_cliccato', step_id: null, tag_id: null },
    etichetta: { tag_id: null, azione: 'solo_se_dentro' },
    ingresso_evento: { tag_id: null },
    fine: {},
  };

  return {
    id: crypto.randomUUID(),
    automation_id: automationId,
    kind,
    label: null,
    config: config[kind],
    next_id: null,
    next_alt_id: null,
    pos_x: posizione.x,
    pos_y: posizione.y,
  };
}
